import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import NavigationBar from '@/components/ui/NavigationBar';
import Button from '@/components/ui/Button';
import Icon from '@/components/AppIcon';
import UGCLayout from '@/components/layouts/UGCLayout';
import { ugcMarketplaceService } from '@/services/ugcMarketplaceService';
import { generateInvoicePDF } from '@/utils/invoiceGenerator';

const navItems = [
  { path: '/app/ugc-brand-dashboard', label: 'Dashboard', icon: 'BarChart3' },
  { path: '/app/ugc-deals', label: 'My Deals', icon: 'FileText' },
  { path: '/app/ugc-invoices-brand', label: 'Invoices', icon: 'DollarSign' },
];

const BrandInvoices = () => {
  const { profile } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadInvoices();
  }, [profile?.id]);

  const loadInvoices = async () => {
    if (!profile?.id) return;
    setLoading(true);
    try {
      const allInvoices = await ugcMarketplaceService.listMyInvoices(profile.id);
      // Filter to brand's sent invoices
      setInvoices(allInvoices.filter(i => i.brand_user_id === profile.id));
    } catch (err) {
      console.error('load invoices failed', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = (invoice) => {
    try {
      generateInvoicePDF(invoice, 'brand');
    } catch (err) {
      console.error('PDF generation failed', err);
    }
  };

  const handleMarkPaid = async (invoiceId) => {
    try {
      await ugcMarketplaceService.updateInvoiceStatus(invoiceId, 'paid');
      loadInvoices();
    } catch (err) {
      console.error('mark paid failed', err);
    }
  };

  const statusBadge = (status) => {
    const colors = {
      draft: 'bg-slate-500/20 text-slate-200',
      sent: 'bg-blue-500/20 text-blue-200',
      paid: 'bg-emerald-500/20 text-emerald-200',
      overdue: 'bg-red-500/20 text-red-200'
    };
    return colors[status] || colors.draft;
  };

  return (
    <UGCLayout>
      <NavigationBar items={navItems} hideLocks />
      <main className="max-w-6xl mx-auto px-4 py-8">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/20 text-blue-100 text-xs font-semibold border border-blue-500/30 mb-3">
            <Icon name="DollarSign" size={14} /> Sent Invoices
          </div>
          <h1 className="text-3xl font-bold text-white">Invoices sent</h1>
          <p className="text-blue-100/80">Track invoices sent to creators after accepting bids.</p>
        </div>

        <div className="mt-8">
          {invoices.length === 0 ? (
            <div className="text-center py-12 bg-slate-800/30 border border-slate-700 rounded-lg">
              <Icon name="FileText" size={48} className="mx-auto text-slate-500 mb-4" />
              <p className="text-slate-400">No invoices sent yet. Accept a bid to send one!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {invoices.map(invoice => (
                <div key={invoice.id} className="bg-slate-800/30 border border-slate-700 rounded-lg p-4 flex justify-between items-center">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <p className="font-semibold text-white">Invoice #{invoice.id?.slice(0, 8).toUpperCase()}</p>
                      <span className={`text-xs px-2 py-1 rounded ${statusBadge(invoice.status)}`}>
                        {invoice.status}
                      </span>
                    </div>
                    <p className="text-sm text-slate-400">
                      Amount: <span className="text-emerald-300 font-semibold">${invoice.amount?.toFixed(2)}</span>
                      {invoice.due_date && ` • Due: ${new Date(invoice.due_date).toLocaleDateString()}`}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDownload(invoice)}
                      className="flex items-center gap-2"
                    >
                      <Icon name="Download" size={16} /> Download
                    </Button>
                    {invoice.status !== 'paid' && (
                      <Button
                        size="sm"
                        onClick={() => handleMarkPaid(invoice.id)}
                        className="flex items-center gap-2"
                      >
                        <Icon name="Check" size={16} /> Mark Paid
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </UGCLayout>
  );
};

export default BrandInvoices;
